<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <div v-if="loading" class="text-center pa-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2">{{ $t("loading") }}</div>
    </div>
    <v-data-table
      v-else-if="report.length"
      :headers="getMobileHeaders(headers)"
      :items="customSearch(filteredTotal, headers, filters.search)"
      :items-per-page="-1"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-food-off </v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_taken_meals_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>
      <template v-slot:item.menu="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ item.menu }}
            </span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.month="{ item }">
        <circle-button
          v-if="item.id !== $t('total')"
          icon="mdi-format-list-bulleted"
          color="primary"
          @click="openDialogReportFirm(item)"
          :tooltip="$t('tooltip_monthly_report')"
        />
      </template>
      <template v-slot:body.append>
        <tr class="font-weight-bold totals-row">
          <td v-if="!device.isMobile"></td>
          <td>{{ $t("totalU") }}</td>
          <td v-if="selectedShifts.includes(1)">
            {{ getTotalForColumn("shift_one") }}
          </td>
          <td v-if="selectedShifts.includes(2)">
            {{ getTotalForColumn("shift_two") }}
          </td>
          <td v-if="selectedShifts.includes(3)">
            {{ getTotalForColumn("shift_three") }}
          </td>
          <td v-if="selectedShifts.includes(4)">
            {{ getTotalForColumn("shift_four") }}
          </td>
          <td>{{ getTotalForColumn("total") }}</td>
          <td></td>
        </tr>
      </template>
    </v-data-table>
    <report-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :company-data="firmReportItem"
    ></report-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsTakenCompanyMonthlyOrders from "@/components/report/meals/dialogs/ReportMealsTakenCompanyMonthlyOrders.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";

export default {
  name: "ReportMealsTabTaken",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasMobileHeaders],
  components: {
    ReportMealsTakenCompanyMonthlyOrders,
  },
  data: () => ({
    loading: false,
    dialogFirm: false,
    firmReportItem: null,
    report: [],
    selectedShifts: [1, 2, 3, 4], // Po default sve smene
  }),
  methods: {
    getTotalShiftMeals() {
      this.loading = true;
      reportService
        .getTotalShiftMeals(this.$store.state.restaurant.id, {
          date: this.filters.dates.from,
          date2: this.filters.dates.to,
        })
        .then((res) => {
          this.report = res.data;
        })
        .catch((error) => {
          console.error("Error loading total shift meals:", error);
          this.report = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    openDialogReportFirm(item) {
      this.dialogFirm = true;
      this.firmReportItem = item;
    },

    getTotalForColumn(column) {
      let totalTaken = 0;
      let totalOrdered = 0;

      this.filteredTotal.forEach((item) => {
        const value = item[column];
        if (typeof value === "string" && value.includes("/")) {
          // Handle "taken/ordered" format like "0/126"
          const parts = value.split("/");
          const taken = parseInt(parts[0]) || 0;
          const ordered = parseInt(parts[1]) || 0;
          totalTaken += taken;
          totalOrdered += ordered;
        } else if (typeof value === "number") {
          totalTaken += value;
        }
      });

      // Always return in "taken/ordered" format to match the data above
      return `${totalTaken}/${totalOrdered}`;
    },
  },
  computed: {
    total() {
      return (this.report || []).map((item, index) => {
        return {
          num: index + 1,
          ...item,
          menu: this.toLatin(item.menu),
        };
      });
    },
    filteredTotal() {
      return this.total.map((item) => {
        const filteredItem = { ...item };

        // Reset shift values based on selected shifts
        if (!this.selectedShifts.includes(1)) filteredItem.shift_one = "0/0";
        if (!this.selectedShifts.includes(2)) filteredItem.shift_two = "0/0";
        if (!this.selectedShifts.includes(3)) filteredItem.shift_three = "0/0";
        if (!this.selectedShifts.includes(4)) filteredItem.shift_four = "0/0";

        // Recalculate total for filtered shifts
        const shifts = ["shift_one", "shift_two", "shift_three", "shift_four"];
        let totalTaken = 0;
        let totalOrdered = 0;

        shifts.forEach((shift, index) => {
          if (this.selectedShifts.includes(index + 1)) {
            const value = item[shift];
            if (typeof value === "string" && value.includes("/")) {
              const parts = value.split("/");
              totalTaken += parseInt(parts[0]) || 0;
              totalOrdered += parseInt(parts[1]) || 0;
            }
          }
        });

        filteredItem.total = `${totalTaken}/${totalOrdered}`;
        return filteredItem;
      });
    },
    filters() {
      return this.$store.state.report.filters;
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("firm"), value: "menu" },
      ];

      const shiftHeaders = [];

      // Dodaj header kolone samo za selektovane smene
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("1st_shift"), value: "shift_one" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("2nd_shift"), value: "shift_two" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("3rd_shift"), value: "shift_three" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("4th_shift"), value: "shift_four" });
      }

      const endHeaders = [
        { text: this.$t("total"), value: "total" },
        { text: this.$t("months"), value: "month" },
      ];

      return [...baseHeaders, ...shiftHeaders, ...endHeaders];
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.getTotalShiftMeals();
      },
      deep: true,
      immediate: true,
    },
    selectedShifts: {
      handler() {
        // Data se automatski filtriraju preko computed property
      },
    },
    isPrinting() {
      if (this.isPrinting) {
        const filteredItems = this.customSearch(
          this.filteredTotal,
          this.headers,
          this.filters.search
        );
        const totalsRow = {
          menu: this.$t("totalU"),
        };

        // Add only selected shifts to totals row
        if (this.selectedShifts.includes(1)) {
          totalsRow.shift_one = this.getTotalForColumn("shift_one");
        }
        if (this.selectedShifts.includes(2)) {
          totalsRow.shift_two = this.getTotalForColumn("shift_two");
        }
        if (this.selectedShifts.includes(3)) {
          totalsRow.shift_three = this.getTotalForColumn("shift_three");
        }
        if (this.selectedShifts.includes(4)) {
          totalsRow.shift_four = this.getTotalForColumn("shift_four");
        }

        totalsRow.total = this.getTotalForColumn("total");

        this.printDocument({
          title: [this.$t("report"), this.$t("taken")].join(": "),
          tables: [
            {
              headers: this.headers.filter((header) => header.value !== "month"),
              items: [...filteredItems, totalsRow],
              showCaption: true,
            },
          ],
          subtitle: this.subtitle,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const filteredItems = this.customSearch(
          this.filteredTotal,
          this.headers,
          this.filters.search
        );
        const totalsRow = {
          menu: this.$t("totalU"),
        };

        // Add only selected shifts to totals row
        if (this.selectedShifts.includes(1)) {
          totalsRow.shift_one = this.getTotalForColumn("shift_one");
        }
        if (this.selectedShifts.includes(2)) {
          totalsRow.shift_two = this.getTotalForColumn("shift_two");
        }
        if (this.selectedShifts.includes(3)) {
          totalsRow.shift_three = this.getTotalForColumn("shift_three");
        }
        if (this.selectedShifts.includes(4)) {
          totalsRow.shift_four = this.getTotalForColumn("shift_four");
        }

        totalsRow.total = this.getTotalForColumn("total");

        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("taken")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("taken"),
              headers: this.headers.filter((header) => header.value !== "month"),
              items: [...filteredItems, totalsRow],
              title: [this.$t("report"), this.$t("taken")].join(" "),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
